import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Globe, Briefcase, Users, Building2,
  Plus, X, Loader2, Shield, HandshakeIcon
} from 'lucide-react';
import { toast } from 'sonner';

// Service badge definitions
const SERVICE_BADGE_TYPES = {
  'service_provider': {
    name: 'Service Provider',
    description: 'Can offer extended services to other offices',
    icon: Briefcase,
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  'service_consumer': {
    name: 'Service Consumer',
    description: 'Can use extended services from other offices',
    icon: HandshakeIcon,
    color: 'bg-violet-100 text-violet-800 border-violet-200',
  },
};

// Display service badges inline
export const ServiceBadges = ({ badges = [], size = 'sm' }) => {
  if (!badges || badges.length === 0) return null;

  return (
    <TooltipProvider>
      <div className="flex flex-wrap gap-1">
        {badges.filter(b => b.is_active !== false).map((badge, idx) => {
          const badgeInfo = SERVICE_BADGE_TYPES[badge.badge_type];
          if (!badgeInfo) return null;
          
          const IconComponent = badgeInfo.icon;
          const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
          
          return (
            <Tooltip key={idx}>
              <TooltipTrigger asChild>
                <div
                  className={`inline-flex items-center justify-center ${size === 'sm' ? 'w-6 h-6' : 'w-7 h-7'} rounded-full border cursor-default ${badgeInfo.color}`}
                  data-testid={`service-badge-icon-${badge.badge_type}`}
                >
                  <IconComponent className={iconSize} />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">{badgeInfo.name}</p>
                <p className="text-xs text-gray-500">{badgeInfo.description}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {badge.scope === 'all' ? 'All Offices' : `${badge.allowed_office_names?.length || 0} offices`}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

// Service Badge management modal
const ServiceBadgeModal = ({ 
  open, 
  onOpenChange, 
  employee, 
  onBadgeUpdate 
}) => {
  const { getAuthHeaders, currentOffice } = useAuth();
  const [offices, setOffices] = useState([]);
  const [currentBadges, setCurrentBadges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  
  // Form state for new badge
  const [selectedBadgeType, setSelectedBadgeType] = useState(null);
  const [scope, setScope] = useState('specific');
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [notes, setNotes] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      // Fetch all offices
      const officesRes = await fetch(`${API_URL}/api/offices/`, { headers });
      if (officesRes.ok) {
        const officesData = await officesRes.json();
        setOffices(officesData);
      }
      
      // Fetch user's service badges
      const badgesRes = await fetch(`${API_URL}/api/service-badges/user/${employee?.user_id || employee?.id}`, { headers });
      if (badgesRes.ok) {
        const badgesData = await badgesRes.json();
        setCurrentBadges(badgesData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && employee) {
      fetchData();
      resetForm();
    }
  }, [open, employee]);

  const resetForm = () => {
    setSelectedBadgeType(null);
    setScope('specific');
    setSelectedOffices([]);
    setNotes('');
  };

  const handleAssignBadge = async () => {
    if (!selectedBadgeType) {
      toast.error('Please select a badge type');
      return;
    }
    
    if (scope === 'specific' && selectedOffices.length === 0) {
      toast.error('Please select at least one office');
      return;
    }
    
    setAssigning(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const payload = {
        user_id: employee?.user_id || employee?.id,
        badge_type: selectedBadgeType,
        scope: scope,
        allowed_offices: scope === 'specific' ? selectedOffices : [],
        notes: notes || null,
      };
      
      const response = await fetch(`${API_URL}/api/service-badges/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success('Service badge assigned successfully');
        resetForm();
        fetchData();
        onBadgeUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign badge');
      }
    } catch (error) {
      console.error('Error assigning badge:', error);
      toast.error('Failed to assign badge');
    } finally {
      setAssigning(false);
    }
  };

  const handleRevokeBadge = async (badgeId) => {
    try {
      const headers = getAuthHeaders();
      
      const response = await fetch(`${API_URL}/api/service-badges/${badgeId}`, {
        method: 'DELETE',
        headers,
      });
      
      if (response.ok) {
        toast.success('Badge revoked successfully');
        fetchData();
        onBadgeUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to revoke badge');
      }
    } catch (error) {
      console.error('Error revoking badge:', error);
      toast.error('Failed to revoke badge');
    }
  };

  const toggleOfficeSelection = (officeId) => {
    setSelectedOffices(prev => 
      prev.includes(officeId) 
        ? prev.filter(id => id !== officeId)
        : [...prev, officeId]
    );
  };

  const selectAllOffices = () => {
    setSelectedOffices(offices.map(o => o.id));
  };

  const deselectAllOffices = () => {
    setSelectedOffices([]);
  };

  const assignedBadgeTypes = currentBadges.filter(b => b.is_active).map(b => b.badge_type);
  const availableBadgeTypes = Object.keys(SERVICE_BADGE_TYPES).filter(t => !assignedBadgeTypes.includes(t));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-badge-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            Cross-Office Service Badges
          </DialogTitle>
          <DialogDescription>
            {employee?.first_name || employee?.full_name} {employee?.last_name || ''} • Manage extended service access
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Badges */}
            <div>
              <Label className="text-sm font-semibold flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-blue-500" />
                Active Service Badges ({currentBadges.filter(b => b.is_active).length})
              </Label>
              
              {currentBadges.filter(b => b.is_active).length === 0 ? (
                <p className="text-sm text-gray-500 italic">No service badges assigned</p>
              ) : (
                <div className="space-y-2">
                  {currentBadges.filter(b => b.is_active).map((badge) => {
                    const badgeInfo = SERVICE_BADGE_TYPES[badge.badge_type];
                    if (!badgeInfo) return null;
                    const IconComponent = badgeInfo.icon;
                    
                    return (
                      <div 
                        key={badge.id}
                        className={`flex items-start justify-between p-3 rounded-lg border ${badgeInfo.color}`}
                      >
                        <div className="flex items-start gap-3">
                          <IconComponent className="h-5 w-5 mt-0.5" />
                          <div>
                            <p className="font-medium">{badgeInfo.name}</p>
                            <p className="text-xs opacity-75 mb-1">{badgeInfo.description}</p>
                            <div className="flex items-center gap-1 text-xs opacity-75">
                              <Building2 className="h-3 w-3" />
                              {badge.scope === 'all' ? (
                                <span>All Offices</span>
                              ) : (
                                <span>{badge.allowed_office_names?.join(', ') || 'No offices selected'}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                          onClick={() => handleRevokeBadge(badge.id)}
                          data-testid={`revoke-badge-${badge.badge_type}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Assign New Badge */}
            {availableBadgeTypes.length > 0 && (
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold flex items-center gap-2 mb-3">
                  <Plus className="h-4 w-4 text-green-500" />
                  Assign New Badge
                </Label>
                
                {/* Badge Type Selection */}
                <div className="space-y-2 mb-4">
                  <Label className="text-xs text-gray-500">Select Badge Type</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {availableBadgeTypes.map((type) => {
                      const badgeInfo = SERVICE_BADGE_TYPES[type];
                      const IconComponent = badgeInfo.icon;
                      const isSelected = selectedBadgeType === type;
                      
                      return (
                        <div
                          key={type}
                          onClick={() => setSelectedBadgeType(isSelected ? null : type)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid={`badge-type-${type}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${badgeInfo.color.split(' ')[0]}`}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{badgeInfo.name}</p>
                              <p className="text-xs text-gray-500">{badgeInfo.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedBadgeType && (
                  <>
                    {/* Office Scope Selection */}
                    <div className="space-y-3 mb-4">
                      <Label className="text-xs text-gray-500">Office Access Scope</Label>
                      <RadioGroup value={scope} onValueChange={setScope} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="all" id="scope-all" />
                          <Label htmlFor="scope-all" className="cursor-pointer flex items-center gap-2">
                            <Globe className="h-4 w-4 text-blue-500" />
                            All Offices
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="specific" id="scope-specific" />
                          <Label htmlFor="scope-specific" className="cursor-pointer flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-500" />
                            Specific Offices
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Office Selection (for specific scope) */}
                    {scope === 'specific' && (
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-gray-500">Select Offices</Label>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={selectAllOffices} className="text-xs h-6">
                              Select All
                            </Button>
                            <Button variant="ghost" size="sm" onClick={deselectAllOffices} className="text-xs h-6">
                              Clear
                            </Button>
                          </div>
                        </div>
                        <ScrollArea className="h-40 border rounded-lg p-2">
                          <div className="space-y-2">
                            {offices.map((office) => (
                              <div 
                                key={office.id}
                                className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50"
                              >
                                <Checkbox
                                  id={`office-${office.id}`}
                                  checked={selectedOffices.includes(office.id)}
                                  onCheckedChange={() => toggleOfficeSelection(office.id)}
                                />
                                <Label 
                                  htmlFor={`office-${office.id}`} 
                                  className="cursor-pointer flex-1 text-sm"
                                >
                                  {office.name}
                                  {office.location && (
                                    <span className="text-xs text-gray-400 ml-2">({office.location})</span>
                                  )}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                        {selectedOffices.length > 0 && (
                          <p className="text-xs text-gray-500">
                            {selectedOffices.length} office(s) selected
                          </p>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    <div className="space-y-2 mb-4">
                      <Label className="text-xs text-gray-500">Notes (optional)</Label>
                      <Textarea
                        placeholder="Add any notes about this badge assignment..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                      />
                    </div>

                    {/* Assign Button */}
                    <Button
                      className="w-full"
                      onClick={handleAssignBadge}
                      disabled={assigning}
                      data-testid="assign-service-badge-btn"
                    >
                      {assigning ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Assigning...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Assign {SERVICE_BADGE_TYPES[selectedBadgeType]?.name}
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceBadgeModal;
